import { useMemo } from 'react';
import { GridContainer, Grid, Card, CardBody, CardHeader, Breadcrumb, BreadcrumbBar } from '@trussworks/react-uswds';
import Icon from '@mdi/react';
import {
  mdiAccountCircle,
  mdiPlayCircle,
  mdiMap,
  mdiClipboardText,
  mdiBookOpenPageVariant,
  mdiClockOutline,
  mdiChevronRight,
} from '@mdi/js';
import { useNavigate, Link } from '@tanstack/react-router';
import { useModal } from '@stores';

import articlesData from './data/articlesData.json';
import faqData from './data/faqData.json';
import './support.scss';

import videosData from './data/videosData.json';
import VideoModal from './modals/VideoModal';
import ProgressiveImage from '@src/app-components/progressive-image/ProgressiveImage';

// Icon mapping for categories
const categoryIconMap = {
  'account-login': mdiAccountCircle,
  'getting-started': mdiPlayCircle,
  'gis-geodatabase': mdiMap,
  'managing-requests': mdiClipboardText,
};

// Color mapping for categories
const categoryColorMap = {
  'account-login': '#0076d6',
  'getting-started': '#04c585',
  'gis-geodatabase': '#8168b3',
  'managing-requests': '#fa9441',
};

// Get featured videos
const getFeaturedVideos = () => {
  const featuredIds = videosData.featuredVideos;
  return videosData.videos.filter((video) => featuredIds.includes(video.id));
};

const SupportGuides = () => {
  const navigate = useNavigate();
  const articles = useMemo(() => articlesData.articles, []);
  const featuredVideos = useMemo(() => getFeaturedVideos(), []);
  const { openModal } = useModal();
  // Group articles by category
  const articlesByCategory = useMemo(() => {
    const grouped = {};
    articles.forEach((article) => {
      if (!grouped[article.category]) {
        const categoryData = faqData.categories.find((c) => c.id === article.category);
        grouped[article.category] = {
          id: article.category,
          title: categoryData?.title || article.category,
          articles: [],
        };
      }
      grouped[article.category].articles.push(article);
    });
    return Object.values(grouped);
  }, [articles]);

  return (
    <div className='support-guides-page'>
      <GridContainer>
        {/* Breadcrumb */}
        <BreadcrumbBar>
          <Breadcrumb>
            <Link to='/support/' className='usa-breadcrumb__link'>
              {' '}
              Support{' '}
            </Link>
          </Breadcrumb>
          <Breadcrumb current>
            <span>Guides</span>
          </Breadcrumb>
        </BreadcrumbBar>

        <div className='guides-header'>
          <h1>Help Guides & Tutorials</h1>
          <p className='guides-description'>
            Step-by-step guides to help you get the most out of the Regulatory Request System.
          </p>
        </div>

        {/* Popular Guides */}
        <section className='guides-section popular-guides'>
          <h2>Popular Guides</h2>
          <Grid row gap='md'>
            {articlesData.popularArticles.map((slug) => {
              const article = articles.find((a) => a.slug === slug);
              if (!article) return null;
              return (
                <Grid key={article.slug} desktop={{ col: 4 }} tablet={{ col: 6 }} mobile={{ col: 12 }}>
                  <Card
                    className='guide-card'
                    onClick={() =>
                      navigate({
                        to: '/support/support-guide-article',
                        search: { slug: article.slug },
                      })
                    }
                  >
                    <CardHeader>
                      <div
                        className='guide-category-badge'
                        style={{ backgroundColor: categoryColorMap[article.category] || '#1b1b1b' }}
                      >
                        <Icon
                          path={categoryIconMap[article.category] || mdiBookOpenPageVariant}
                          size={0.8}
                          color='#fff'
                        />
                      </div>
                      <h3>{article.title}</h3>
                    </CardHeader>
                    <CardBody>
                      <p className='guide-summary'>{article.summary}</p>
                      <div className='guide-meta'>
                        <span className='guide-read-time'>
                          <Icon path={mdiClockOutline} size={0.7} />
                          {article.readTime}
                        </span>
                        <Icon path={mdiChevronRight} size={0.8} className='guide-arrow' />
                      </div>
                    </CardBody>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </section>
        {/* Video Tutorials */}
        <section className='guides-section'>
          <GridContainer>
            <h2>Video Tutorials</h2>
            <p className='section-description'>Watch step-by-step video guides to help you navigate the RRS.</p>
            <Grid row gap='md'>
              {featuredVideos.map((video) => (
                <Grid key={video.id} desktop={{ col: 3 }} tablet={{ col: 6 }} mobile={{ col: 12 }}>
                  <Card className='support-video-card'>
                    <div className='video-thumbnail'>
                      <ProgressiveImage
                        alt={`${video.title} video thumbnail`}
                        className='video-image'
                        localPath={`video-stills/${video.image}`}
                        hasAvif
                        hasWebp
                      />
                      <span className='video-duration'>{video.duration}</span>
                    </div>
                    <CardBody>
                      <h4>{video.title}</h4>
                      <p>{video.description.substring(0, 100)}...</p>
                      <button
                        type='button'
                        className='video-play-link'
                        onClick={() => openModal(VideoModal, { title: video.title, videoUrl: video.url })}
                      >
                        Watch Video
                      </button>
                    </CardBody>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </GridContainer>
        </section>

        {/* All Guides by Category */}
        <section className='guides-section all-guides'>
          <h2>All Guides</h2>
          {articlesByCategory.map((category) => (
            <div key={category.id} className='guides-category'>
              <h3 className='category-title'>
                <Icon
                  path={categoryIconMap[category.id] || mdiBookOpenPageVariant}
                  size={1}
                  style={{ color: categoryColorMap[category.id] || '#1b1b1b' }}
                />
                {category.title}
              </h3>
              <div className='guides-list'>
                {category.articles.map((article) => (
                  <button
                    key={article.slug}
                    className='guide-list-item'
                    onClick={() =>
                      navigate({
                        to: '/support/support-guide-article',
                        search: { slug: article.slug },
                      })
                    }
                  >
                    <div className='guide-list-content'>
                      <span className='guide-list-title'>{article.title}</span>
                      <span className='guide-list-summary'>{article.summary}</span>
                    </div>
                    <div className='guide-list-meta'>
                      <span className='guide-read-time'>
                        <Icon path={mdiClockOutline} size={0.7} />
                        {article.readTime}
                      </span>
                      <Icon path={mdiChevronRight} size={0.8} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </section>
      </GridContainer>
    </div>
  );
};

export default SupportGuides;
